/* eslint-disable no-console */
import React, {useEffect, useRef, useState} from 'react';

import OtpVerificationView from '../../../components/OtpVerificationView/OtpVerificationView';
import {
  resendForgotPasswordOTP,
  verifyForgotPasswordOTP
} from '../../../services/modules/User/forgotPassword';
import {IOtpFormData} from '../Utils/Interfaces';

const OtpVerification: React.FC<IOtpFormData> = ({
  email,
  onFinish,
  onChangeEmail
}) => {
  const RESEND_OTP_TIMER = 120; // 2 minutes
  const [otp, setOtp] = useState<string>('');
  const [otpError, setOtpError] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(RESEND_OTP_TIMER);
  const [verifyingOTPAction, setVerifyingOTPAction] = useState<boolean>(false);
  const [resendingOTPAction, setResendingOTPAction] = useState<boolean>(false);

  const inputRefs = useRef<any>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Start timer
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setOtpError(false);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Initial focus and timer start
  useEffect(() => {
    inputRefs.current?.[0]?.input?.focus();
    startTimer();
  }, []);

  useEffect(() => {
    if (
      otp.length === 6 &&
      !verifyingOTPAction &&
      !resendingOTPAction &&
      !otpError &&
      timer > 0
    ) {
      void handleVerify();
    }
  }, [otp]);

  const handleResendOtp = async () => {
    setOtp('');
    setOtpError(false);
    setTimer(RESEND_OTP_TIMER);
    inputRefs.current?.[0]?.input?.focus();
    startTimer();

    setResendingOTPAction(true);
    try {
      await resendForgotPasswordOTP({email});
    } catch {
      setOtpError(true);
    } finally {
      setResendingOTPAction(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setOtpError(true);

      return;
    }

    setVerifyingOTPAction(true);
    setOtpError(false);

    try {
      const data = await verifyForgotPasswordOTP({
        email,
        password: '',
        otp
      });

      if (data?.status === 'success') {
        onFinish(otp);
      } else {
        setOtpError(true);
      }
    } catch {
      setOtpError(true);
    } finally {
      setVerifyingOTPAction(false);
    }
  };

  return (
    <OtpVerificationView
      email={email}
      otp={otp}
      otpError={otpError}
      timer={timer}
      verifyingOTPAction={verifyingOTPAction}
      resendingOTPAction={resendingOTPAction}
      onOtpChange={(value) => {
        setOtp(value);
        setOtpError(false);
      }}
      onVerify={() => {
        void handleVerify();
      }}
      onResendOtp={() => {
        void handleResendOtp();
      }}
      onChangeEmail={onChangeEmail}
      inputRef={inputRefs}
      buttonLabel="Continue"
    />
  );
};

export default OtpVerification;
